import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { icon } from '../lib/icons';
import { t, LocaleController } from '../lib/i18n';
import { inputHints } from '../lib/field-hints';
import { Dictation, voiceSupported, joinText } from '../lib/voice';

/** <nfc-search> — premium search field with optional voice input. Fires
 *  `search` (detail: current value). Voice is capability-gated (Chrome/Android)
 *  and dictates live into the field, filtering as you speak. */
@customElement('nfc-search')
export class NfcSearch extends LitElement {
  @property() placeholder = '';
  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);
  @property() value = '';
  @state() private listening = false;

  private dictation = new Dictation();
  private voiceBase = '';
  private lastInterim = '';

  static styles = css`
    :host{ display:block; }
    .field{ display:flex; align-items:center; gap:10px; height:48px; padding:0 6px 0 16px;
      background:var(--app-surface-2,#eef1f5); border:1px solid transparent; border-radius:var(--radius-full,999px);
      transition:background var(--dur-2,180ms), border-color var(--dur-2,180ms), box-shadow var(--dur-2,180ms); }
    .field:focus-within{ background:var(--app-surface,#fff); border-color:var(--app-primary,#5a57e6);
      box-shadow:0 0 0 3px color-mix(in srgb, var(--app-primary) 20%, transparent); }
    .field.listening{ border-color:var(--app-primary,#5a57e6); box-shadow:0 0 0 3px color-mix(in srgb, var(--app-primary) 26%, transparent); }
    .ico{ color:var(--app-muted,#576070); font-size:22px; }
    input{ flex:1; min-width:0; border:none; outline:none; background:none; font-family:var(--font-plain,system-ui);
      font-size:15px; color:var(--app-text,#14161b); }
    /* The field has its own clear button — the browser's built-in search ✕
       beside it reads as two competing clears. */
    input::-webkit-search-cancel-button{ -webkit-appearance:none; display:none; }
    input::placeholder{ color:var(--app-faint,#808a99); }
    .clear{ border:none; background:none; cursor:pointer; display:none; align-items:center; justify-content:center;
      width:32px; height:32px; border-radius:999px; color:var(--app-muted); }
    .clear:hover{ background:var(--app-surface-3); }
    :host([data-has-value]) .clear{ display:flex; }
    .mic{ position:relative; flex:none; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center;
      width:36px; height:36px; border-radius:999px; color:var(--app-muted,#576070); background:none; transition:color var(--dur-2,180ms), background var(--dur-2,180ms); }
    .mic:hover{ background:var(--app-surface-3,#e6e9ef); color:var(--app-primary,#5a57e6); }
    .mic.on{ color:#fff; background:var(--app-primary,#5a57e6); }
    .mic.on::after{ content:''; position:absolute; inset:0; border-radius:999px; border:2px solid var(--app-primary,#5a57e6); animation:ring 1.8s ease-out infinite; }
    @keyframes ring{ from{ transform:scale(1); opacity:.6; } to{ transform:scale(1.7); opacity:0; } }
    @media (prefers-reduced-motion: reduce){ .mic.on::after{ animation:none; } }
    .mic .ico{ font-size:20px; color:inherit; }
  `;

  get voiceSupported(): boolean { return voiceSupported(); }

  private emit() {
    this.toggleAttribute('data-has-value', !!this.value);
    this.dispatchEvent(new CustomEvent<string>('search', { detail: this.value, bubbles: true, composed: true }));
  }

  private onInput(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
    this.emit();
  }

  private clear() {
    this.stopVoice();
    this.value = '';
    this.removeAttribute('data-has-value');
    const input = this.renderRoot.querySelector('input');
    if (input) input.value = '';
    this.dispatchEvent(new CustomEvent<string>('search', { detail: '', bubbles: true, composed: true }));
  }

  // ── Voice search ──────────────────────────────────────────────
  private toggleVoice() {
    if (this.listening) { this.stopVoice(); return; }
    this.voiceBase = this.value;
    this.lastInterim = '';
    this.listening = true;
    this.dictation.start({
      onResult: (finalChunk, interim) => {
        if (finalChunk) this.voiceBase = joinText(this.voiceBase, finalChunk);
        this.lastInterim = interim;
        this.setValue(interim ? joinText(this.voiceBase, interim) : this.voiceBase);
      },
      onEnd: () => this.finishVoice(),
    });
  }

  private finishVoice() {
    if (!this.listening) return;
    if (this.lastInterim.trim()) this.voiceBase = joinText(this.voiceBase, this.lastInterim);
    this.setValue(this.voiceBase);
    this.lastInterim = '';
    this.listening = false;
  }

  private stopVoice() {
    if (this.dictation.listening) this.dictation.stop();
    this.finishVoice();
  }

  /** Write dictated text into the field and filter live. */
  private setValue(v: string) {
    this.value = v;
    const input = this.renderRoot.querySelector('input');
    if (input) input.value = v;
    this.emit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopVoice();
  }

  render() {
    return html`<div class=${classMap({ field: true, listening: this.listening })}>
      ${icon('search')}
      <input ${inputHints('search')} .value=${this.value} placeholder=${this.listening ? t('search.listening') : this.placeholder}
        @input=${this.onInput} aria-label=${this.placeholder} />
      <button class="clear" @click=${this.clear} aria-label=${t('search.clear')}>${icon('close')}</button>
      ${this.voiceSupported
        ? html`<button class=${classMap({ mic: true, on: this.listening })} @click=${this.toggleVoice}
            aria-label=${t(this.listening ? 'search.stopVoice' : 'search.byVoice')}>
            ${icon(this.listening ? 'stop' : 'mic')}
          </button>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'nfc-search': NfcSearch; }
}
